<script lang="ts" setup>

import {LoyaltyCardMembership} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {computed, ref, watch} from "vue";
import {loyaltyCardMembershipCreditApi} from "@/container";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter, DialogTrigger, DialogClose
} from "@/core/components/ui/dialog";
import BaseButton from "@/core/components/base/BaseButton.vue";
import LoyaltyCardMembershipPointCredit
    from "@/customer/components/customer-loyalty-membership/LoyaltyCardMembershipPointCredit.vue";
import LoyaltyCardService from "@/loyalty-card/services/LoyaltyCardService";
import LoyaltyCardMembershipProductCredit, {ProductCreditValue}
    from "@/customer/components/customer-loyalty-membership/LoyaltyCardMembershipProductCredit.vue";
import {useToastStore} from "@/stores/toast.store";
import {isAxiosError} from "axios";

const emit = defineEmits(['credited']);
const loading = ref(false);
const props = defineProps<{
    membership: LoyaltyCardMembership;
    customerId: number;
}>();

const isPointCard = computed(() => LoyaltyCardService().hasPointRewards(props.membership.loyalty_card));

const open = ref(false);
const pointCredit = ref<number>(0);
const purchase = ref<ProductCreditValue>({product_id: null, quantity: 1});
/** Remounts the form, so a reopened dialog never shows the previous credit. */
const formKey = ref(0);
const toast = useToastStore();

watch(open, (isOpen) => {
    if (!isOpen) {
        return;
    }

    pointCredit.value = 0;
    purchase.value = {product_id: null, quantity: 1};
    formKey.value++;
});

const canSubmit = computed(() => (
    isPointCard.value ? pointCredit.value > 0 : purchase.value.product_id !== null && purchase.value.quantity > 0
));

/**
 * The message shown when a credit fails. A product the card would not credit
 * is refused with the server's reason, which the pharmacist has to read.
 */
const creditError = (error: unknown): string => {
    if (isAxiosError(error) && error.response?.status === 422 && error.response.data?.message) {
        return error.response.data.message;
    }

    return 'Une erreur est survenue lors de l\'ajout du crédit';
};

/**
 * Submit the credit: a balance credit for point cards, a hand-recorded
 * purchase of a product the card would credit for product cards.
 */
const submit = async () => {
    loading.value = true;

    const request = isPointCard.value
        ? loyaltyCardMembershipCreditApi()
            .create(props.customerId, props.membership.id, {type: 'point', value: pointCredit.value})
        : loyaltyCardMembershipCreditApi()
            .createPurchase(props.customerId, props.membership.id, {
                product_id: purchase.value.product_id as number,
                quantity: purchase.value.quantity
            });

    return request
        .then(() => {
            toast.showSuccess({
                content: 'Le crédit a été ajouté avec succès'
            });
            emit('credited');
            open.value = false;
        })
        .catch((error) => {
            toast.showError({
                content: creditError(error)
            });
        })
        .finally(() => {
            loading.value = false;
        });
};
</script>

<template>
  <div>
    <Dialog v-model:open="open">
      <DialogTrigger as-child>
        <div>
          <button
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Créditer
          </button>
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader class="text-start">
          <DialogTitle>
            Créditer
          </DialogTitle>
        </DialogHeader>
        <div class="space-y-4">
          <LoyaltyCardMembershipPointCredit
            v-if="isPointCard"
            :key="formKey"
            :model-value="pointCredit"
            @update:model-value="(value) => pointCredit = value"
          />
          <LoyaltyCardMembershipProductCredit
            v-else
            :key="formKey"
            v-model="purchase"
            :membership="membership"
          />
        </div>
        <DialogFooter>
          <DialogClose as-child>
            <BaseButton>
              Fermer
            </BaseButton>
          </DialogClose>
          <BaseButton
            :loading="loading"
            :disabled="!canSubmit"
            primary
            type="submit"
            @click="submit"
          >
            Valider
          </BaseButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
